import { OperatorElearningTopicCategoryTranslationService } from '../../../../../api/api-services/operator-elearning-topic-category-translation.service';
import { IOperatorElearningTopicCategoryTranslationIm } from '../../../../../api/operator-elearning-topic-category-translation-im.model';
import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { OperatorElearningTopicCategoryService } from '../../../../../api/api-services/operator-elearning-topic-category.service';
import { IOperatorElearningTopicCategoryIm } from '../../../../../api/operator-elearning-topic-category-im.model';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { OperatorAvailableExamsService } from '../../../../../api/api-services/operator-available-exams.service';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { IExamViewModel } from '../../../../../api/exam-view-model.model';
import { eModalRoles } from '../../../../shared/models/modals';
import { Component, OnInit, inject } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-category-create',
  templateUrl: './category-create.component.html',
  styleUrl: './category-create.component.scss'
})
export class CategoryCreateComponent implements OnInit {

  private _operatorElearningTopicCategoryTranslationService = inject(OperatorElearningTopicCategoryTranslationService);
  private _operatorElearningTopicCategoriesService = inject(OperatorElearningTopicCategoriesService);
  private _operatorElearningTopicCategoryService = inject(OperatorElearningTopicCategoryService);
  private _operatorAvailableExamsService = inject(OperatorAvailableExamsService);
  private _messageService = inject(MessageService);
  private _dialogRef = inject(DynamicDialogRef);
  private _fb = inject(FormBuilder);

  public availableExams: IExamViewModel[] = [];
  public categoryForm!: FormGroup;
  public maxTranslations = 3;
  public loading = false;
  
  public translationLanguages = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'Angielski' },
    { code: 'uk', name: 'Ukraiński' },
    { code: 'de', name: 'Niemiecki' }
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadData();
    this.initializeOrder();
  }

  private initializeOrder(): void {
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories) => {
        const maxOrder = categories.length > 0
          ? Math.max(...categories.map(c => c.order || 0))
          : 0;
        const nextOrder = maxOrder + 1;
        this.categoryForm.patchValue({ order: nextOrder });
      },
      error: (error) => {
        console.error('Błąd podczas pobierania działów:', error);
        this.categoryForm.patchValue({ order: 1 });
      }
    });
  }

  private initForm(): void {
    this.categoryForm = this._fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      isActive: [true],
      order: [null, [Validators.required, Validators.min(0)]],
      translations: this._fb.array([]),
      selectedExams: [[]]
    });
  }

  get translations(): FormArray {
    return this.categoryForm.get('translations') as FormArray;
  }

  public addNewTranslationForm(): void {
    if (this.translations.length < this.maxTranslations) {
      const translationGroup = this._fb.group({
        languageCode: ['', [Validators.required]],
        name: ['', [Validators.required, Validators.minLength(2)]],
        collapsed: [false]
      });
      this.translations.push(translationGroup);
    }
  }

  public removeTranslationForm(index: number): void {
    this.translations.removeAt(index);
  }

  public toggleTranslationCollapse(index: number): void {
    const translation = this.translations.at(index);
    if (translation) {
      const currentCollapsed = translation.get('collapsed')?.value;
      translation.get('collapsed')?.setValue(!currentCollapsed);
    }
  }

  public isTranslationCollapsed(index: number): boolean {
    const translation = this.translations.at(index);
    return translation ? translation.get('collapsed')?.value : false;
  }

  public canAddMoreTranslations(): boolean {
    return this.translations.length < this.maxTranslations;
  }

  public onLanguageChange(index: number, newLanguage: string): void {
  }

  public addNewTranslation(languageCode: string, name: string): void {
  }

  public addTranslation(): void {
  }

  private loadData(): void {
    this.loading = true;
    
    this._operatorAvailableExamsService.get().subscribe({
      next: (exams) => {
        this.availableExams = exams;
        this.loading = false;
      },
      error: (error) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się załadować dostępnych egzaminów'
        });
        this.loading = false;
      }
    });
  }


  public canSaveCategory(): boolean {
    if (!this.categoryForm.valid) {
      return false;
    }

    for (let i = 0; i < this.translations.length; i++) {
      const translation = this.translations.at(i);
      const languageCode = translation.get('languageCode')?.value;
      const name = translation.get('name')?.value;

      if (languageCode && (!name || name.trim().length < 2)) {
        return false;
      }
    }

    return true;
  }

  public saveCategory(): void {
    if (this.canSaveCategory()) {
      this.loading = true;
      
      for (let i = this.translations.length - 1; i >= 0; i--) {
        const translation = this.translations.at(i);
        const languageCode = translation.get('languageCode')?.value;
        const name = translation.get('name')?.value;
        
        if (!languageCode || !name) {
          this.translations.removeAt(i);
        }
      }
      
      const categoryData: IOperatorElearningTopicCategoryIm = {
        name: this.categoryForm.get('name')?.value,
        order: this.categoryForm.get('order')?.value || 0,
        isActive: this.categoryForm.get('isActive')?.value ,
        examsGuid: this.categoryForm.get('selectedExams')?.value || []
      };

      this._operatorElearningTopicCategoryService.post(categoryData).subscribe({
        next: (newCategory) => {
          const translations = this.translations.value;
          if (translations.length > 0) {
            this.saveTranslations(newCategory.id, translations, newCategory);
          } else {
            this.completeCategoryCreation(newCategory);
          }
        },
        error: (error) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się utworzyć nowego działu'
          });
          this.loading = false;
        }
      });
    }
  }

  private saveTranslations(categoryId: number, translations: any[], category: IOperatorElearningTopicCategoryVm): void {
    let savedCount = 0;
    const totalTranslations = translations.length;

    translations.forEach((translation) => {
      const translationData: IOperatorElearningTopicCategoryTranslationIm = {
        elearningTopicCategoryId: categoryId,
        name: translation.name,
        languageCode: translation.languageCode
      };

      this._operatorElearningTopicCategoryTranslationService.post(translationData).subscribe({
        next: () => {
          savedCount++;
          if (savedCount === totalTranslations) {
            this.completeCategoryCreation(category);
          }
        },
        error: (error) => {
          savedCount++;
          if (savedCount === totalTranslations) {
            this.completeCategoryCreation(category);
          }
        }
      });
    });
  }

  private completeCategoryCreation(category: IOperatorElearningTopicCategoryVm): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Nowy dział został dodany'
    });
    this.closeDialog(eModalRoles.SUCCESS);
  }

  public closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this._dialogRef.close(status);
  }

  public getLanguageName(languageCode: string): string {
    const language = this.translationLanguages.find(lang => lang.code === languageCode);
    return language ? language.name : languageCode;
  }

public getAvailableLanguagesForTranslation(currentIndex?: number) {
  const used = this.translations.controls
    .map((control, idx) => idx === currentIndex ? null : control.get('languageCode')?.value)
    .filter((code): code is string => !!code);

  return this.translationLanguages.filter(lang =>
    lang.code !== 'pl' && !used.includes(lang.code)
  );
}
}
